import React, { useState } from "react";
import styled, { css } from "styled-components";
import defaultProfilePicture from "@assets/images/variant-jaangal/defaults/defautOrganizationPicture.svg";
import { ImageVersions } from "@domain/media/dtos";
import { Media } from "@domain/media/media";
import { PersonType } from "@domain/user/user";
import { row } from "@ui/components/layout";
import { ImageView } from "@ui/components/media/imageView";
import { CircleSkeleton } from "@ui/components/skeleton/basicSkeletons";

export type ImageSize = "S" | "M" | "L" | "XL" | "default";

export const imageSizes: { [key in ImageSize]: number } = {
	XL: 110,
	L: 60,
	M: 44,
	S: 21,
	default: 30,
};
const emojiSizes: { [key in ImageSize]: number } = {
	XL: 50,
	L: 26,
	M: 20,
	S: 9,
	default: 13,
};

const getSize = (size: ImageSize | number): number => (typeof size === "number" ? size : imageSizes[size]);
const getEmojiSize = (size: ImageSize | number): number =>
	typeof size === "number" ? (size > 60 ? emojiSizes["XL"] : emojiSizes["default"]) : emojiSizes[size];

interface ProfilePictureProps extends React.HTMLAttributes<HTMLDivElement> {
	picture?: Media;
	size?: ImageSize | number;
	fallbackType?: PersonType;
	empty?: boolean;
	shadowed?: boolean;
	defaultUrl?: string;
	alt?: string;
}
export const ProfilePicture: React.FC<ProfilePictureProps> = ({
	picture,
	size = "default",
	fallbackType,
	empty,
	defaultUrl,
	alt,
	...props
}) => {
	const [fallback, setFallback] = useState(false);

	const isOrganization = !picture && (fallbackType === "INSTITUTE" || fallbackType === "COMPANY");
	const numericSize = getSize(size);
	const numericEmojiSize = getEmojiSize(size);

	return (
		<Relative
			size={numericSize}
			hasBorder={(!picture || fallback) && isOrganization}
			className="profile-picture"
			{...props}
		>
			{picture && !fallback ? (
				<ImageView
					alt={alt}
					cover
					zoomDisabled
					image={picture}
					imageVersion={ImageVersions.SMALL}
					onError={() => setFallback(true)}
				/>
			) : (
				<ProfilePictureImg src={empty || isOrganization ? undefined : defaultProfilePicture}>
					{isOrganization && <Emoji size={numericEmojiSize}>{fallbackType === "INSTITUTE" ? "🎓" : "🏢"}</Emoji>}
				</ProfilePictureImg>
			)}
		</Relative>
	);
};

export const ProfilePictureSkeleton: React.FC<ProfilePictureProps> = ({ size = "default", ...htmlProps }) => {
	return <CircleSkeleton {...htmlProps} size={getSize(size)} />;
};

const Relative = styled.div.withConfig({
	shouldForwardProp: (prop) => !["size", "shadowed", "hasBorder"].includes(prop),
})<{ size: number; shadowed?: boolean; hasBorder?: boolean }>`
	position: relative;
	flex-shrink: 0;
	overflow: hidden;
	/* Fixes Safari rounded images for some reason https://gist.github.com/ayamflow/b602ab436ac9f05660d9c15190f4fd7b */
	-webkit-mask-image: -webkit-radial-gradient(white, black);
	background-color: ${({ theme }) => theme.colors.white};
	border: solid;
	border-color: ${({ theme }) => theme.colors.lightergray};

	${({ size, shadowed, hasBorder }) => css`
		width: ${size}px;
		height: ${size}px;
		border-radius: ${size / 2}px;
		${shadowed && "box-shadow: 0 2px 14px 0 rgba(0, 0, 0, 0.2)"};
		border-width: ${hasBorder ? 1 : 0}px;
	`}
`;

const ProfilePictureImg = styled.div.withConfig({
	shouldForwardProp: (prop) => !["src", "hidden"].includes(prop),
})<{ src?: string; hidden?: boolean }>`
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	${row(0, "center", "center")};
	${({ hidden }) => hidden && "opacity: 0"};
	transition: opacity 0.2s linear;

	${({ src }) =>
		src &&
		css`
			background-color: transparent;
			background-repeat: no-repeat;
			background-image: url(${src});
			background-position: 50% 50%;
			background-size: cover;
		`}
`;

const Emoji = styled.span.withConfig({
	shouldForwardProp: (prop) => prop !== "size",
})<{ size: number }>`
	font-size: ${({ size }) => size}px;
	vertical-align: middle;
	text-align: center;
`;
